import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { useMute } from './MuteContext';

import { ReactComponent as Play } from 'assert/images/Common/play.svg';
import { ReactComponent as Pause } from 'assert/images/Common/pause.svg';
import { ReactComponent as Replay } from 'assert/images/Common/replay.svg';
import { ReactComponent as Expand } from 'assert/images/Common/expand.svg';
import { ReactComponent as Mute } from 'assert/images/Common/volumeMute.svg';
import { ReactComponent as UnMute } from 'assert/images/Common/volumeUnMute.svg';
import { ReactComponent as BackArrow } from 'assert/images/Common/backArrow.svg';
import { ReactComponent as Video } from 'assert/images/Common/video.svg';
import { Capacitor } from '@capacitor/core';
import { Skeleton } from '@mui/material';

const BackButton = styled.button`
  position: fixed;
  top: env(safe-area-inset-top, 10px);
  left: env(safe-area-inset-left, 10px);
  background: rgba(0, 0, 0, 0.4);
  border: none;
  border-radius: 20px;
  padding: 8px;
  z-index: 10000;
  margin: 16px 8px;

  svg {
    width: 26px;
    height: 26px;
    fill: white;
  }
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-height: 500px;
  background: black;
  overflow: hidden;
  cursor: pointer;
  border-radius: 2px;
  -webkit-tap-highlight-color: transparent;
  &.fullscreen {
    max-height: none;
  }
`;

const StyledReactPlayer = styled(ReactPlayer)`
  width: 100%;
  max-height: 500px;
  border-radius: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  &.fullscreen {
    max-height: none;
  }

  video {
    width: 100%;
    height: auto;
    max-height: 500px;
    object-fit: contain;
    border-radius: 2px;
  }
  &.fullscreen video {
    height: 100%;
    max-height: none;
  }
`;

const ControlsWrapper = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: rgba(20, 20, 20, 0.8);
  display: flex;
  flex-direction: column;
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
`;

const SeekBar = styled.input.attrs({ type: 'range' })`
  width: 100%;
  height: 4px;
  appearance: none;
  background: transparent;
  outline: none;
  cursor: pointer;
  margin: 0;

  &::-webkit-slider-runnable-track {
    height: 4px;
    background: linear-gradient(
      to right,
      #ccc 0%,
      #ccc ${({ value }) => value * 100}%,
      #555 ${({ value }) => value * 100}%,
      #555 100%
    );
    border-radius: 2px;
  }

  &::-webkit-slider-thumb {
    appearance: none;
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background: #f00;
    margin-top: -4px;
    position: relative;
    z-index: 3;
  }

  &::-moz-range-track {
    height: 4px;
    background: #555;
    border-radius: 2px;
  }

  &::-moz-range-progress {
    background-color: #ffffff;
    height: 4px;
  }

  &::-moz-range-thumb {
    height: 12px;
    width: 12px;
    border-radius: 50%;
    background: #f00;
    border: none;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  margin-right: 10px;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    fill: white;
  }
`;

const TimeText = styled.span`
  font-size: 14px;
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 4px;

  .current {
    color: white;
  }

  .duration {
    color: #ccc;
  }
`;

const ReplayOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  display: ${({ show }) => (show ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ show }) => (show ? 1 : 0)};
  pointer-events: ${({ show }) => (show ? 'auto' : 'none')};

  button {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    padding: 15px;
    border-radius: 50%;
    cursor: pointer;

    svg {
      width: 30px;
      height: 30px;
      fill: white;
    }
  }
`;

const CenteredLogo = styled(Video)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  path {
    stroke: #5f5f5f;
  }
`;

const CustomVideoPlayer = ({ src, stopAutoPlay }) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const videoId = useRef(`vid_${Math.random().toString(36).slice(2, 10)}`);

  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showReplay, setShowReplay] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isMobilePlatform, setIsMobilePlatform] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { volume, toggleMute, version, activeVideoId, setActiveVideoId } =
    useMute();

  const isActive = activeVideoId === videoId.current && isVisible;

  useEffect(() => {
    const internal = playerRef.current?.getInternalPlayer?.();
    if (!internal) return;
    internal.volume = volume;
    internal.muted = volume === 0;
    setIsMuted(volume === 0);
  }, [version]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          setActiveVideoId(videoId.current);
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.8 }
    );

    const el = containerRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [setActiveVideoId]);

  useEffect(() => {
    const internal = playerRef.current?.getInternalPlayer?.();
    if (!internal) return;

    if (isActive) {
      if (!stopAutoPlay) {
        internal.play?.().catch(() => {});
        setPlaying(true);
      }
    } else {
      internal.pause?.();
      setPlaying(false);
    }
  }, [isActive]);

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    setIsMobilePlatform(platform === 'ios' || platform === 'android');
  }, []);

  useEffect(() => {
    if (showControls && !isHovered) {
      const timer = setTimeout(() => setShowControls(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showControls, isHovered]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(1, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleReplay = () => {
    setShowReplay(false);
    playerRef.current.seekTo(0);
    setActiveVideoId(videoId.current);
    setPlaying(true);
  };

  const handleToggleMute = (e) => {
    e.stopPropagation();
    const internal = playerRef.current?.getInternalPlayer?.();
    const willBeMuted = volume !== 0;
    setIsMuted(willBeMuted);
    toggleMute();
    setTimeout(() => {
      if (internal) {
        internal.volume = willBeMuted ? 0 : 0.5;
        internal.muted = willBeMuted;
      }
    }, 10);
  };

  const handlePlayToggle = (e) => {
    e.stopPropagation();
    setActiveVideoId(videoId.current);
    setPlaying((p) => !p);
    setShowControls(true);
  };

  return (
    <>
      {isFullScreen && isMobilePlatform && (
        <BackButton
          aria-label="Exit Fullscreen"
          onClick={(e) => {
            e.stopPropagation();
            setIsFullScreen(false);
            setPlaying(false);
          }}
        >
          <BackArrow />
        </BackButton>
      )}
      <Container
        ref={containerRef}
        onClick={handlePlayToggle}
        onMouseEnter={() => {
          if (!isMobilePlatform) setIsHovered(true);
        }}
        onMouseLeave={() => {
          if (!isMobilePlatform) setIsHovered(false);
        }}
        className={isFullScreen && isMobilePlatform ? 'fullscreen' : ''}
        style={
          isFullScreen && isMobilePlatform
            ? {
                position: 'fixed',
                top: 'env(safe-area-inset-top)',
                bottom: 'env(safe-area-inset-bottom)',
                left: 0,
                height:
                  'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
                width: '100vw',
                background: 'black',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : {}
        }
      >
        {isLoading && (
          <div
            style={{
              position: 'relative',
            }}
          >
            <Skeleton variant="rectangular" height={500} animation="wave" />
            <CenteredLogo />
          </div>
        )}

        <StyledReactPlayer
          ref={playerRef}
          url={src}
          playing={isActive && playing}
          volume={volume}
          loop={false}
          width="100%"
          height="100%"
          controls={false}
          onReady={() => setIsLoading(false)}
          config={{
            file: {
              attributes: {
                playsInline: true,
                autoPlay: !stopAutoPlay,
              },
            },
          }}
          onProgress={({ played }) => setPlayed(played)}
          onDuration={(d) => setDuration(d)}
          onEnded={() => {
            setShowReplay(true);
          }}
          onPlay={() => setShowReplay(false)}
          className={isFullScreen && isMobilePlatform ? 'fullscreen' : ''}
        />

        <ReplayOverlay show={showReplay}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleReplay();
            }}
          >
            <Replay />
          </button>
        </ReplayOverlay>
        {!showControls && !isHovered && isMobilePlatform && (
          <IconButton
            aria-label="Toggle Mute"
            onClick={handleToggleMute}
            style={{
              position: 'absolute',
              bottom: 9,
              right: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              borderRadius: 20,
              padding: 8,
              zIndex: 99,
              transition: 'opacity 0.3s ease-in-out',
              opacity: !showControls && !isHovered ? 1 : 0,
              pointerEvents: !showControls && !isHovered ? 'auto' : 'none',
            }}
          >
            {isMuted ? <Mute /> : <UnMute />}
          </IconButton>
        )}
        {!isLoading && (
          <ControlsWrapper visible={showControls || isHovered}>
            <SeekBar
              value={played}
              step={0.01}
              min={0}
              max={1}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setShowReplay(false);
                setPlayed(val);
                playerRef.current.seekTo(val);
              }}
            />

            <Controls>
              <IconButton onClick={handlePlayToggle}>
                {isActive && playing ? <Pause /> : <Play />}
              </IconButton>

              <TimeText>
                <span className="current">{formatTime(played * duration)}</span>
                /<span className="duration">{formatTime(duration)}</span>
              </TimeText>

              <IconButton aria-label="Toggle Mute" onClick={handleToggleMute}>
                {isMuted ? <Mute /> : <UnMute />}
              </IconButton>

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMobilePlatform) {
                    setIsFullScreen((prev) => !prev);
                  } else {
                    const el = playerRef.current.getInternalPlayer();
                    el?.requestFullscreen?.() || el?.webkitEnterFullscreen?.();
                  }
                }}
              >
                <Expand />
              </IconButton>
            </Controls>
          </ControlsWrapper>
        )}
      </Container>
    </>
  );
};

export default CustomVideoPlayer;
